import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import Input from '@mui/material/Input'
import InputAdornment from '@mui/material/InputAdornment'
import SearchIcon from '@mui/icons-material/Search'
import CreateFileFolderForm from 'components/CreateFileFolderForm'
import FileFolderCard from 'components/FileFolderCard'
import PPSwitch from 'components/PPSwitch'
import { FileFolder } from 'interfaces'
import { Fragment, memo, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { ColorGradient } from 'utils/colors'
import styles from './MultiUploadManager.pcss'

const FILTER_VISIBLE_FOR_FOLDERS_COUNT = 5

export interface UploadTargetPanelProps {
  folders: FileFolder[]
  mode: 'create' | 'save'
  filter: string
  selectedFolders: { [id: string]: boolean }
  isCreatingFolder: boolean
  filesCount: number
  images: string[]
  onModeChange: (mode: 'create' | 'save') => void
  onFilterChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onFolderClick: (folder: FileFolder) => void
  onClose: () => void
  onStartUpload: () => void
  onCreateFolder: (
    name: string,
    color: ColorGradient,
    isPrivate?: boolean,
    featuredImage?: string | File,
    coverImages?: string[]
  ) => boolean
}

export const UploadTargetPanel = memo(function UploadTargetPanel({
  folders,
  mode,
  filter,
  selectedFolders,
  isCreatingFolder,
  filesCount,
  images,
  onModeChange,
  onFilterChange,
  onFolderClick,
  onClose,
  onStartUpload,
  onCreateFolder
}: UploadTargetPanelProps) {
  const intl = useIntl()

  const modeOptions = useMemo(() => {
    const options = [{ value: 'create', label: 'New folder' }]
    if (folders.length > 0) {
      options.push({ value: 'save', label: 'Existing folder' })
    }
    return options
  }, [folders.length])

  const visibleFolders = useMemo(
    () => folders.filter(f => f.name.indexOf(filter) !== -1),
    [folders, filter]
  )
  const hasSelectedFolders = Object.values(selectedFolders).filter(Boolean).length > 0

  return (
    <div className={styles['panel-right']}>
      <div className={styles['heading-title']}>
        <FormattedMessage
          id="uploads.popup.title"
          values={{
            count: filesCount,
            typeSelect: (
              <PPSwitch
                options={modeOptions}
                selectedValue={mode}
                withButtonStyles
                className={styles.select}
                onSelectedValueChange={onModeChange as any}
              />
            )
          }}
        />
        {mode === 'save' && folders.length > FILTER_VISIBLE_FOR_FOLDERS_COUNT && (
          <div className={styles['search-wrapper']}>
            <FormControl>
              <Input
                value={filter}
                startAdornment={(
                  <InputAdornment position="start">
                    <SearchIcon className={styles['icon-search']} />
                  </InputAdornment>
                )}
                classes={{ input: styles['input-root'] }}
                onChange={onFilterChange}
                placeholder={intl.formatMessage({ id: 'uploads.hint.search-folders' })}
              />
            </FormControl>
          </div>
        )}
      </div>
      <div>
        {mode === 'save' && (
          <Fragment>
            {folders.length > 0 ? (
              <Fragment>
                <div className={styles['folders-grid-wrapper']}>
                  <div className={styles['folders-grid']}>
                    {visibleFolders.map(folder => {
                      const selected = selectedFolders[folder.id]
                      return (
                        <div className={styles['folder-box']} key={folder.id}>
                          <div className={`${styles['folder-border']} ${selected ? styles.selected : ''}`}>
                            <FileFolderCard folder={folder} disableNavigation className={styles.folder} onClick={onFolderClick} />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
                <div className={styles.actions}>
                  <Button variant="text" onClick={onClose}>
                    <FormattedMessage id="actions.cancel" />
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!hasSelectedFolders}
                    className={styles['btn-save']}
                    onClick={onStartUpload}
                  >
                    <FormattedMessage id="actions.save" />
                  </Button>
                </div>
              </Fragment>
            ) : (
              <div className={styles['msg-empty']}>
                <FormattedMessage id="uploads.popup.msg-no-folders" />
              </div>
            )}
          </Fragment>
        )}

        {mode === 'create' && (
          <CreateFileFolderForm
            loading={isCreatingFolder}
            filesCount={filesCount}
            images={images}
            onCancel={onClose}
            onSubmit={onCreateFolder}
          />
        )}
      </div>
    </div>
  )
})

export default UploadTargetPanel
